/**
 * cdVersion.js — which Congressional District boundary vintage (CD119 vs CD120)
 * the map should draw for a given (schema, wave).
 *
 * WHY THIS MATTERS: the map polygons must match the CD coding of the data
 * (rows join to features by `area_id`, derived from the shapefile GEOID). ACC
 * models are projected onto CD120 (the districts for the upcoming elections);
 * all other current schemas are on CD119. Drawing the wrong vintage would draw
 * the wrong district shapes under the data. This is a correctness concern, not
 * a cosmetic one — so the switch is automatic (no user toggle).
 *
 * RULES (extend CD_RULES to add schemas or move a cutoff):
 *   - ACC data is always CD120.
 *   - Tracking is always CD120. It used to be CD119 for waves 1–3 and CD120
 *     from wave 4, because that is how the waves were originally modelled. The
 *     tracking export was then re-run so that EVERY wave is projected onto
 *     CD120, which is what makes wave-over-wave district movement a
 *     like-for-like comparison (all waves now carry an identical set of 436
 *     dist_fips). Leaving the old cutoff in place would have drawn wave 3 on
 *     119th-Congress shapes under CD120-coded data.
 *   - Everything else stays on CD119.
 *
 * Both FGB files live in /usmaps and expose a `GEOID` property (the join key),
 * so switching files does not change how rows match features. They differ only
 * in the district field name (CD119FP vs CD120FP), handled in geoUtils labels.
 */

export const CD_VERSIONS = {
  CD119: {
    label: "119th Congress",
    short: "CD119",
    fgb: "/usmaps/us_cds_2024_simplified.fgb",
  },
  CD120: {
    label: "120th Congress",
    short: "CD120",
    fgb: "/usmaps/us_cds_2024_simplified_CD120.fgb",
    // Topology-preserving 12% simplification (mapshaper), 2.2MB vs 15.3MB.
    // Served to phone-width viewports, where the full-detail boundaries are
    // indistinguishable and the download dominates load time.
    fgbMobile: "/usmaps/us_cds_2024_simplified_CD120_mobile.fgb",
  },
};

const DEFAULT_VERSION = "CD119";

// Per-schema resolution. `fixed` ignores wave; `fromWave` (inclusive) is the
// first wave that flips `before` → `after`. Schemas not listed use DEFAULT.
const CD_RULES = {
  acc:      { fixed: "CD120" },
  tracking: { fixed: "CD120" },
};

/**
 * @param {string|undefined|null} schema  physical schema ("acc", "tracking", ...)
 * @param {number|string|null}    wave     selected wave (null when not applicable)
 * @returns {"CD119"|"CD120"}
 */
export function cdVersionFor(schema, wave) {
  const rule = CD_RULES[schema];
  if (!rule) return DEFAULT_VERSION;
  if (rule.fixed) return rule.fixed;
  const w = Number(wave);
  return Number.isFinite(w) && w >= rule.fromWave ? rule.after : rule.before;
}

/**
 * Public-relative FGB path for the resolved vintage.
 * Pass { mobile: true } to get the lighter phone variant where one exists
 * (falls back to the full-detail file for vintages without one).
 */
export function cdFgbPathFor(schema, wave, { mobile = false } = {}) {
  const version = CD_VERSIONS[cdVersionFor(schema, wave)];
  return (mobile && version.fgbMobile) || version.fgb;
}

/** Display metadata ({ label, short, fgb }) for the resolved vintage. */
export function cdVersionInfoFor(schema, wave) {
  return CD_VERSIONS[cdVersionFor(schema, wave)];
}
